import { useMemo, useCallback } from 'react'
import { useLatest } from 'react-use'
import { useModelClickListenerV2 } from '@scene'
import { isEqual, some } from 'lodash-es'
import { useControlStore, useModelStore } from '@editor/stores'
import { multiEditConfig } from '@editor/utils'
import { EventTypes } from 'src/components/scene/ModelMeshV2/state/store'
import { isPointerModifierPressed } from 'src/shortcuts/pointerModifiers'
import { useGuidToElementIncludingRips, useMarqueeSelectionListener } from '.'
import useModelClickListeners from './useModelClickListeners'
import useMultiSelectionEvent from './useMultiSelectionEvent'

interface Options {
  addElements: (elementIds: string[]) => void
  removeElement: (elementId: string) => void
  selectedElements: string[]
}

/**
 * Right now implementation of selected elements differs from view to view
 * (results/structural planning). Right now we do not have one approach that is
 * reused throughout the app. The idea of this hook is to have one centralised
 * interface to call for multi selection purposes that can be used to have one
 * final approach in the future. This will work by gradually moving to the same
 * behaviour (e.g. using the same stores instead of the query params for the
 * selected elements).
 *
 * Behaviour: While shift key is active multi selection will be active. Every
 * element clicked in the scene will be added to selection
 */
const useMultiSelection = ({ addElements, removeElement, selectedElements }: Options) => {
  const multiSelection = useMultiSelectionEvent()

  const model = useModelStore(state => state.model)

  const actionMode = useControlStore(state => state.actionMode)

  const guidToElement = useGuidToElementIncludingRips()
  const openingGuidToOpeningWithWall = useMemo(
    () =>
      model.walls.reduce((acc, wall) => {
        ;(wall.openings || []).forEach((opening: Opening) => {
          acc[opening.guid] = { opening, wall, indexInWall: 0 }
        })
        return acc
      }, {} as Record<string, { opening: Opening; wall: ShapeObject; indexInWall: number }>),
    [model.walls],
  )

  const guidToElementIncludingOpenings = useMemo(() => {
    return {
      ...guidToElement,
      ...openingGuidToOpeningWithWall,
    }
  }, [guidToElement, openingGuidToOpeningWithWall])

  const shouldBeAdded = useCallback(
    (guid: string, selectedElementGuids: string[]) => {
      const elements = [guid, ...selectedElementGuids].map(
        guid => guidToElementIncludingOpenings[guid],
      )

      // necessary to prevent adding elements that are not in the model (e.g. stiffening intervals)
      if (some(elements, element => !element)) return false

      const config = multiEditConfig(elements)

      return some(Object.values(config))
    },
    [guidToElementIncludingOpenings],
  )

  const eventTypes = useMemo(() => ['position', 'opening'] as EventTypes[], [])

  useModelClickListeners(
    event => {
      multiSelection(
        event.object.name,
        selectedElements,
        guid => addElements([guid]),
        removeElement,
        shouldBeAdded,
      )
    },
    [addElements, selectedElements, shouldBeAdded],
    true,
    false,
    true,
  )

  useModelClickListenerV2(
    eventTypes,
    guid => {
      multiSelection(
        guid,
        selectedElements,
        guid => addElements([guid]),
        removeElement,
        shouldBeAdded,
      )
    },
    { enabled: actionMode !== 'hide', guids: null, enableOnMultiSelect: true },
  )

  const selectedElementsRef = useLatest(selectedElements)
  const addElementsRef = useLatest(addElements)
  const shouldBeAddedRef = useLatest(shouldBeAdded)

  useMarqueeSelectionListener(
    ids => {
      if (isPointerModifierPressed('multiSelect') && !isEqual(ids, selectedElementsRef.current)) {
        const elementsToAdd: string[] = []
        for (const id of ids) {
          if (shouldBeAddedRef.current(id, [...selectedElementsRef.current, ...elementsToAdd])) {
            elementsToAdd.push(id)
          }
        }

        addElementsRef.current(elementsToAdd)
      }
    },
    { enabled: true },
  )

  return {
    selectedElements,
  }
}

export default useMultiSelection
